import axios from "axios";

const baseURL = process.env.REACT_APP_BACKEND_URL || "";
const IS_DEV = process.env.NODE_ENV !== "production";

export const api = axios.create({
  baseURL,
  // Critical: tell the browser to send + accept Set-Cookie on cross-origin
  // calls. For the FyreCRM same-origin deployment this is also safe.
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export function resolveApiUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (!baseURL) return path;
  return `${baseURL.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

// Endpoints that should NOT trigger a global "unauthenticated" event
// when they return 401 (they are expected to do so in normal flows).
const AUTH_SILENT_PATHS = ["/api/auth/login", "/api/auth/me", "/api/auth/logout"];

// Simple pub/sub for global auth events. The AuthContext subscribes to this.
const listeners = new Set();
export function onUnauthenticated(handler) {
  listeners.add(handler);
  return () => listeners.delete(handler);
}
function emitUnauthenticated() {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore listener errors */
    }
  }
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.debug("[api]", error?.config?.method?.toUpperCase(), url, "->", status);
    }
    // Auto-logout: an authenticated route returned 401. The AuthContext will
    // clear local state so ProtectedRoute redirects to /login.
    if (status === 401 && !AUTH_SILENT_PATHS.some((p) => url.includes(p))) {
      emitUnauthenticated();
    }
    return Promise.reject(error);
  },
);

export function extractApiError(err, fallback = "Ein Fehler ist aufgetreten.") {
  if (!err) return fallback;
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return fallback;
}
